// Analyze quiz answers → archetype + color & trend recommendations
// Pure function. No side effects. Spanish content (matching the quiz audience).

const ARCHETYPES = {
  editorial: {
    name: "La Editorial Magnética",
    headline: "Naciste para una portada.",
    description:
      "Hay personas que entran a una habitación y otras que la rediseñan. Tu look no es accesorio, es declaración. Funcionas mejor cuando el maquillaje te acompaña a transformarte — no a maquillarte. Te queda el riesgo bien calculado: una mirada construida, un labio que cuenta una historia, una piel que se ve real y a la vez intencional.",
    affinity: ["Drama elegante", "Concepto + intención", "Cámara editorial"],
    avoid: "Looks tímidos o demasiado novia genérica.",
    image: "https://images.unsplash.com/photo-1557924760-b14a6d4a4ef3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  romantica: {
    name: "La Romántica Luminosa",
    headline: "El maquillaje como caricia.",
    description:
      "Te ven y piensan: 'qué bien envejece la elegancia'. Tu fuerza no grita, susurra. Prefieres que el maquillaje destape lo que ya hay — la luz natural, la piel buena, los ojos que ya conoces. Las novias clásicas, las invitadas de mil bodas, las que aparecen en las fotos sin esfuerzo, son tu tribu.",
    affinity: ["Pieles luminosas", "Tonos cálidos y suaves", "Mirada limpia y profunda"],
    avoid: "Capas excesivas, brillos exagerados.",
    image: "https://images.unsplash.com/photo-1503236823255-94609f598e71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  clasica: {
    name: "La Clásica Atemporal",
    headline: "Audrey, Grace, tu abuela en sus 20s.",
    description:
      "Tienes un instinto natural por lo que no pasa de moda. Sabes que las modas son una distracción y prefieres los códigos que se ven igual de bien hoy que en 30 años cuando vuelvas a abrir el álbum. Tu maquillaje es preciso: una línea bien puesta, un labio mate justo, una piel impecable. Nada más, nada menos.",
    affinity: ["Líneas pulidas", "Mate sofisticado", "Tonos atemporales"],
    avoid: "Tendencias estridentes que te pongan fecha de caducidad.",
    image: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  minimalista: {
    name: "La Minimalista Moderna",
    headline: "Menos pero mejor.",
    description:
      "Te incomoda lo cargado. Crees — y con razón — que la mejor versión de ti es la que se nota poco pero se siente todo. Buscas piel viva, ojos despiertos, labios humectados. Tu maquillaje no se ve, se intuye. Eres la respuesta a la pregunta '¿cómo le hace para verse así de descansada?'.",
    affinity: ["Skin first", "Nudes humanos", "Bones structure"],
    avoid: "Pestañas pesadas, contornos marcados, labiales mate planos.",
    image: "https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
};

const PALETTES = {
  warm: {
    name: "Cálida Bronce",
    desc: "Tonos que multiplican la luz cálida de tu piel: durazno tostado, terracota, cinnamon, gold honey.",
    swatches: [
      { hex: "#C9846B", label: "Terracota" },
      { hex: "#E1B387", label: "Durazno" },
      { hex: "#A66B49", label: "Cinnamon" },
      { hex: "#D9A66A", label: "Honey gold" },
    ],
  },
  cool: {
    name: "Fría Editorial",
    desc: "Tonos que contrastan y enmarcan: plum, mauve frío, rose taupe, vino seco.",
    swatches: [
      { hex: "#7A5A6B", label: "Mauve frío" },
      { hex: "#5D3A4A", label: "Plum" },
      { hex: "#B89197", label: "Rose taupe" },
      { hex: "#7E2F38", label: "Vino seco" },
    ],
  },
  neutral: {
    name: "Neutra Pulida",
    desc: "Beiges complejos, taupe, café tostado y un solo acento para romper la calma.",
    swatches: [
      { hex: "#C9B59B", label: "Beige cálido" },
      { hex: "#8B6D5E", label: "Café tostado" },
      { hex: "#A89684", label: "Taupe" },
      { hex: "#5A3E36", label: "Espresso" },
    ],
  },
  jewel: {
    name: "Joya Profunda",
    desc: "Tonos saturados que se ven impecables bajo luz cálida y en cámara: bordó, esmeralda, plum, oro líquido.",
    swatches: [
      { hex: "#6B1F2A", label: "Bordó" },
      { hex: "#2F5D4A", label: "Esmeralda" },
      { hex: "#5D2A47", label: "Plum profundo" },
      { hex: "#B8943F", label: "Oro líquido" },
    ],
  },
};

// Trend suggestions library — short, specific looks
const TRENDS = {
  editorial_eye: { title: "Editorial eye gráfico", desc: "Una sola línea de color sobre el párpado — sin sombra extra. Funciona increíble con piel muy limpia." },
  glossy_lips: { title: "Labios glossy nutridos", desc: "Labial cremoso + gloss transparente encima. Vuelve a estar en todas las pasarelas." },
  cherry_lip: { title: "Cherry lip stained", desc: "Labio rojo bitten — como si llevaras horas con él. Perfecto si quieres impacto sin esfuerzo." },
  skin_first: { title: "Skin-first finish", desc: "El maquillaje empieza y termina en la piel. Highlighter cremoso, base translúcida, cero polvos." },
  bronze_smokey: { title: "Bronze smokey", desc: "Smokey en tonos bronce/cobre en lugar de negro. Halaga muchísimo a las pieles cálidas." },
  soft_glam: { title: "Soft glam con halo eye", desc: "Sombra clara al centro del párpado, oscura en los extremos. Hace los ojos enormes en foto." },
  monochrome: { title: "Monocromático suave", desc: "Mismo tono de blush, labio y párpado. Limpio, moderno, deja respirar la piel." },
  high_shine: { title: "High-shine glam", desc: "Glitter contenido + iluminador líquido. Para entradas memorables." },
  retro_liner: { title: "Delineado retro", desc: "Cat eye preciso, labio nude o rojo. Atemporal." },
  freckled_skin: { title: "Pecas dibujadas", desc: "Pecas hechas a mano para un look natural y soñador — funciona en cualquier estación." },
};

const TRENDS_BY_ARCHETYPE = {
  editorial: ["editorial_eye", "high_shine", "bronze_smokey"],
  romantica: ["soft_glam", "glossy_lips", "monochrome"],
  clasica: ["retro_liner", "cherry_lip", "skin_first"],
  minimalista: ["skin_first", "monochrome", "freckled_skin"],
};

// ─────────────────────────────────────────────
// Scoring
// ─────────────────────────────────────────────
export function analyzeQuiz(answers) {
  let drama = 0;
  let romance = 0;
  let warmth = 0;
  let energy = 0;

  // Q1 — emotion
  if (answers.q1 === "alegria") energy += 2;
  if (answers.q1 === "anticipacion") energy += 2;
  if (answers.q1 === "nervios") energy += 1;
  if (answers.q1 === "calma") energy -= 2;

  // Q2 — style
  if (answers.q2 === "minimalista") drama -= 2;
  if (answers.q2 === "romantico") { romance += 2; warmth += 1; }
  if (answers.q2 === "editorial") drama += 2;
  if (answers.q2 === "clasico") { drama -= 1; romance += 1; }
  if (answers.q2 === "atrevido") { drama += 2; energy += 1; }

  // Q3 — transformation
  if (answers.q3 === "tu-mas") drama -= 1;
  if (answers.q3 === "transformada") drama += 2;
  if (answers.q3 === "epoca") { romance += 1; drama += 1; }
  if (answers.q3 === "sofisticada") drama += 1;
  if (answers.q3 === "magnetica") { drama += 2; energy += 1; }

  // Q4 — worry (no axis impact, just informational)

  // Q5 — ideal moment
  if (answers.q5 === "intimo") romance += 2;
  if (answers.q5 === "entrada") { drama += 2; energy += 1; }
  if (answers.q5 === "fotos") drama += 1;
  if (answers.q5 === "baile") energy += 2;

  // Q6 — palette
  if (answers.q6 === "tierras") warmth += 2;
  if (answers.q6 === "joya") { warmth += 1; drama += 1; }
  if (answers.q6 === "rosas") { warmth += 1; romance += 1; }
  if (answers.q6 === "frescos") warmth -= 2;
  if (answers.q6 === "metalicos") { warmth += 1; drama += 1; }

  // Q7 — flowers (mostly mood)
  if (answers.q7 === "peonias") romance += 1;
  if (answers.q7 === "rosas") romance += 1;
  if (answers.q7 === "girasoles") energy += 1;
  if (answers.q7 === "silvestres") warmth -= 1;

  // Q8 — hair
  if (answers.q8 === "trenza") drama += 1;
  if (answers.q8 === "recogido") drama += 1;
  if (answers.q8 === "ondas") romance += 1;

  // Q10 — makeup
  if (answers.q10 === "glow") drama -= 1;
  if (answers.q10 === "smokey") drama += 2;
  if (answers.q10 === "labio-rojo") drama += 2;
  if (answers.q10 === "glam") { drama += 2; energy += 1; }
  if (answers.q10 === "neutro") drama -= 2;

  // Archetype decision
  let archetypeKey;
  if (drama <= -2) archetypeKey = "minimalista";
  else if (drama >= 4) archetypeKey = "editorial";
  else if (romance >= 3) archetypeKey = "romantica";
  else archetypeKey = "clasica";

  // Palette decision
  let paletteKey;
  if (warmth >= 3 && drama >= 1) paletteKey = "jewel";
  else if (warmth >= 2) paletteKey = "warm";
  else if (warmth <= -1) paletteKey = "cool";
  else paletteKey = "neutral";

  const archetype = ARCHETYPES[archetypeKey];
  const palette = PALETTES[paletteKey];
  const trends = (TRENDS_BY_ARCHETYPE[archetypeKey] || []).map((k) => TRENDS[k]);

  return {
    archetypeKey,
    archetype,
    paletteKey,
    palette,
    trends,
    scores: { drama, romance, warmth, energy },
  };
}
